import { ColumnDef } from '@tanstack/react-table';
import {
  Icon,
  IconButton,
  SortButton,
  Tags,
  Typography,
} from '@wisecare-tech/design-system-web';
import storeDev from '~/data/store';
import { iStore } from '~/domain/interfaces/models';
import { IconFlag } from '~/presentation/base/icons';
import { priorityMap } from '~/utils/mapPriority';
import { calculateAgeLaudo } from '~/utils/tableDateLaudo';
import { Information, Priority } from './styles';
import { iOnCallRequest } from '~/domain/interfaces/models/OnCallRequest';
import { iOnCallRequestRecords } from '~/domain/usecases/onCallRequest/remote';
import { makeReduxActiveMessage } from '~/main/factories/usecases/message/Update';
import { closeModal } from '~/utils/closeModal';
import { History } from '~/main/routes';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { makeRemoteReactivateOnCallRequest } from '~/main/factories/usecases/onCallRequest/ReactivateOnCallRequestFactory';
import { AlertMessage } from '~/presentation/components/messages/AlertMessage';
import { Popover } from '~/presentation/components/UI/popover';
import { makeRemoteDownloadClinicalDocs } from '~/main/factories/usecases/clinicalDocs/DownloadClinicalDocsFactory';

type Variant =
  | 'white'
  | 'blue'
  | 'green'
  | 'red'
  | 'purple'
  | 'pink'
  | 'orange';

export const columns_finalized: ColumnDef<iOnCallRequestRecords>[] = [
  {
    accessorKey: 'consultant',
    header: ({ column }) => <SortButton column={column}>Paciente</SortButton>,
    cell: ({ row }) => {
      const data = row.getValue(
        'consultant',
      ) as iOnCallRequest['records'][0]['consultant'];

      return (
        <Information>
          <Typography variant="b4_14regular">{data?.fullname}</Typography>
          <Typography variant="b4_14regular" style={{ color: '#656A6E' }}>
            {calculateAgeLaudo(data?.birthdate)}
          </Typography>
        </Information>
      );
    },
  },
  {
    accessorKey: 'onCall',
    header: ({ column }) => {
      return <SortButton column={column}>Especialidade</SortButton>;
    },
    cell: ({ row }) => {
      const onCall = row.getValue(
        'onCall',
      ) as iOnCallRequest['records'][0]['onCall'];

      return <Typography variant="b4_14regular">{onCall.name}</Typography>;
    },
  },
  {
    accessorKey: 'updated',
    header: ({ column }) => (
      <SortButton column={column}>Última atualização</SortButton>
    ),
    cell: ({ row }) => {
      const updated = row.getValue(
        'updated',
      ) as iOnCallRequest['records'][0]['updated'];

      function formatDate(dateString: string): { date: string; hour: string } {
        const date = parseISO(dateString);

        return {
          date: format(date, 'dd/MM/yyyy', { locale: ptBR }),
          hour: format(date, 'HH:mm', { locale: ptBR }),
        };
      }

      return (
        <Information>
          <Typography variant="b4_14regular">
            {formatDate(updated)?.date}
          </Typography>
          <Typography variant="b4_14regular" style={{ color: '#656A6E' }}>
            {formatDate(updated)?.hour}
          </Typography>
        </Information>
      );
    },
  },
  {
    accessorKey: 'outcome',
    header: ({ column }) => <SortButton column={column}>Desfecho</SortButton>,
    cell: ({ row }) => {
      const outcome = row.getValue(
        'outcome',
      ) as iOnCallRequest['records'][0]['outcome'];

      const status: Record<string, string> = {
        RETURN: 'Retorno',
        TRANSFER: 'Transferência',
        DEATH: 'Óbito',
        PERMANENCE: 'Permanência',
        REFERRAL: 'Encaminhamento',
        CLINICAL_DISCHARGE: 'Alta clínica',
      };

      const statusColor: Record<string, string> = {
        DEATH: 'white',
        CLINICAL_DISCHARGE: 'green',
        REFERRAL: 'purple',
        RETURN: 'pink',
        PERMANENCE: 'blue',
      };

      return (
        <Information>
          <Tags
            variant={statusColor[outcome?.msgKey] as Variant}
            text={status[outcome?.msgKey] as string}
          />
        </Information>
      );
    },
  },
  {
    id: 'actions',
    enableHiding: true,
    cell: ({ row }) => {
      const value = row.original;

      const contentPopover = (
        <div
          style={{
            width: '100%',
            backgroundColor: 'white',
            flexDirection: 'column',
            padding: '8px 16px',
          }}
        >
          <div
            style={{
              display: 'flex',
              height: 48,
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
            }}
            onClick={() => {
              makeReduxActiveMessage().active({
                active: 'twoButtonModal',
                actionCancel: () => {
                  closeModal();
                },
                data: {
                  variant: 'INFO',
                  message:
                    'Sua solicitação foi cancelada pelo profissional e agora você está reenviando para um novo atendimento. Os dados enviados pela primeira vez ficarão o mesmo. Tem certeza que deseja continuar?',
                  textSecondaryButton: 'Cancelar',
                  textPrimaryButton: 'Sim, quero reenviar',
                  title: 'Reenviar solicitação',
                },
                actionOk: () => {
                  makeRemoteReactivateOnCallRequest()
                    .reactivate({
                      onCallId: value?.onCall?.id,
                      requesterId: value?.requester?.id,
                      requestId: value?.id,
                    })
                    .then(() => {
                      AlertMessage({
                        message: 'Solicitação reenviada com sucesso!',
                        type: 'success',
                      });
                      closeModal();
                    })
                    .catch(() => {
                      AlertMessage({
                        message: 'Erro ao reenviar solicitação!',
                        type: 'danger',
                      });
                    });
                },
              });
            }}
          >
            <Icon name="refresh" />
            <Typography variant="b3_14medium">Reenviar solicitação</Typography>
          </div>
          {value?.currentAppointment.medicalOpinion && (
            <div
              style={{
                display: 'flex',
                height: 48,
                alignItems: 'center',
                gap: 8,
                cursor: 'pointer',
              }}
              onClick={() => {
                if (value)
                  makeRemoteDownloadClinicalDocs()
                    .download({
                      docId: Number(value.currentAppointment.medicalOpinion),
                    })
                    .then(response => {
                      const fileURL = URL.createObjectURL(response as Blob);

                      // Abre o arquivo em uma nova guia
                      window.open(fileURL);
                    })
                    .catch(error => {
                      console.log('>>> Error: ', error);
                      AlertMessage({
                        message: error?.message ?? 'Erro ao baixar arquivo',
                        type: 'danger',
                      });
                    });
              }}
            >
              <Icon name="download" />
              <Typography variant="b3_14medium">
                Baixar parecer médico
              </Typography>
            </div>
          )}
        </div>
      );

      const store: iStore = storeDev.getState();

      const url = window.location.pathname;

      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <IconButton
            icon="visibility"
            variant="primary"
            size="sm"
            onClick={() => {
              History.getHistory().push('/appointment/detail', {
                appointmentId: value?.currentAppointment?.id,
                from: url.replaceAll('/', ''),
              });
            }}
          />
          <Popover content={contentPopover} originX="left" originY="top">
            <IconButton icon="more_vert" variant="primary" size="sm" />
          </Popover>
        </div>
      );
    },
  },
];
